/**
 * Model Import Utilities
 * 
 * Provides utilities for working with GLB/GLTF models including:
 * - Automatic bounding box calculation
 * - Scale normalization
 * - Animation discovery
 * - Physics shape fitting
 * 
 * Use this when importing new models to determine correct settings.
 */

import { Scene } from "@babylonjs/core/scene";
import { SceneLoader } from "@babylonjs/core/Loading/sceneLoader";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";

// Import GLTF loader
import "@babylonjs/loaders/glTF";

/**
 * Model analysis result
 */
export interface ModelAnalysis {
    /** Original bounding box dimensions */
    originalSize: { width: number; height: number; depth: number };
    /** Original bounding box center */
    originalCenter: Vector3;
    /** Total vertex count */
    vertexCount: number;
    /** Number of meshes */
    meshCount: number;
    /** Animation names found */
    animations: string[];
    /** Whether the model has a skeleton */
    hasRig: boolean;
    /** Bone count if rigged */
    boneCount: number;
    /** Recommended scale to fit target height */
    recommendedScale: number;
    /** Suggested physics shape */
    suggestedPhysicsShape: "box" | "sphere" | "capsule" | "cylinder";
    /** Suggested physics dimensions */
    suggestedPhysicsDimensions: { width: number; height: number; depth: number };
}

/**
 * Analyze a GLB/GLTF model and return sizing/animation information
 * 
 * @param scene - Babylon scene
 * @param path - Path to the model file
 * @param targetHeight - Desired height in world units (default: 2 for humanoid)
 * @returns Analysis results with recommended settings
 */
export async function analyzeModel(
    scene: Scene,
    path: string,
    targetHeight: number = 2
): Promise<ModelAnalysis> {
    const directory = path.substring(0, path.lastIndexOf("/") + 1);
    const filename = path.substring(path.lastIndexOf("/") + 1);

    const result = await SceneLoader.ImportMeshAsync("", directory, filename, scene);

    // Calculate combined bounding box
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    let totalVertices = 0;

    result.meshes.forEach(mesh => {
        if (mesh.getTotalVertices() > 0) {
            totalVertices += mesh.getTotalVertices();
            
            mesh.computeWorldMatrix(true);
            const boundingInfo = mesh.getBoundingInfo();
            const worldMin = boundingInfo.boundingBox.minimumWorld;
            const worldMax = boundingInfo.boundingBox.maximumWorld;

            minX = Math.min(minX, worldMin.x);
            minY = Math.min(minY, worldMin.y);
            minZ = Math.min(minZ, worldMin.z);
            maxX = Math.max(maxX, worldMax.x);
            maxY = Math.max(maxY, worldMax.y);
            maxZ = Math.max(maxZ, worldMax.z);
        }
    });

    const width = maxX - minX;
    const height = maxY - minY;
    const depth = maxZ - minZ;
    const center = new Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);

    // Calculate recommended scale
    const recommendedScale = targetHeight / height;

    // Determine physics shape
    let suggestedPhysicsShape: "box" | "sphere" | "capsule" | "cylinder" = "box";
    const aspectRatio = height / Math.max(width, depth);
    
    if (aspectRatio > 1.5) {
        // Tall and thin - capsule for characters
        suggestedPhysicsShape = "capsule";
    } else if (Math.abs(width - depth) < 0.1 * width && Math.abs(width - height) < 0.1 * width) {
        // Roughly cubic - sphere might work
        suggestedPhysicsShape = "sphere";
    } else if (Math.abs(width - depth) < 0.2 * width && aspectRatio > 1) {
        // Round but tall - cylinder
        suggestedPhysicsShape = "cylinder";
    }

    // Get animation names
    const animations = result.animationGroups.map(ag => ag.name);

    // Get skeleton info
    const hasRig = result.skeletons.length > 0;
    const boneCount = hasRig ? result.skeletons[0].bones.length : 0;

    // Clean up loaded meshes (this was just for analysis)
    result.meshes.forEach(mesh => mesh.dispose());
    result.skeletons.forEach(skeleton => skeleton.dispose());
    result.animationGroups.forEach(ag => ag.dispose());

    return {
        originalSize: { width, height, depth },
        originalCenter: center,
        vertexCount: totalVertices,
        meshCount: result.meshes.length,
        animations,
        hasRig,
        boneCount,
        recommendedScale,
        suggestedPhysicsShape,
        suggestedPhysicsDimensions: {
            width: width * recommendedScale,
            height: height * recommendedScale,
            depth: depth * recommendedScale,
        },
    };
}

/**
 * Calculate the bounding box of loaded meshes
 */
export function calculateBoundingBox(meshes: AbstractMesh[]): {
    min: Vector3;
    max: Vector3;
    size: Vector3;
    center: Vector3;
} {
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    meshes.forEach(mesh => {
        if (mesh.getTotalVertices() > 0) {
            mesh.computeWorldMatrix(true);
            const boundingInfo = mesh.getBoundingInfo();
            const worldMin = boundingInfo.boundingBox.minimumWorld;
            const worldMax = boundingInfo.boundingBox.maximumWorld;

            minX = Math.min(minX, worldMin.x);
            minY = Math.min(minY, worldMin.y);
            minZ = Math.min(minZ, worldMin.z);
            maxX = Math.max(maxX, worldMax.x);
            maxY = Math.max(maxY, worldMax.y);
            maxZ = Math.max(maxZ, worldMax.z);
        }
    });

    const min = new Vector3(minX, minY, minZ);
    const max = new Vector3(maxX, maxY, maxZ);
    
    return {
        min,
        max,
        size: max.subtract(min),
        center: min.add(max).scale(0.5),
    };
}

/**
 * Calculate scale factor to fit a model to a target height
 */
export function calculateScaleForHeight(currentHeight: number, targetHeight: number): number {
    if (currentHeight <= 0) return 1;
    return targetHeight / currentHeight;
}

/**
 * Calculate scale factor to fit within a bounding box
 */
export function calculateScaleToFit(
    currentSize: { width: number; height: number; depth: number },
    maxSize: { width: number; height: number; depth: number }
): number {
    const scaleX = maxSize.width / currentSize.width;
    const scaleY = maxSize.height / currentSize.height;
    const scaleZ = maxSize.depth / currentSize.depth;
    return Math.min(scaleX, scaleY, scaleZ);
}

/**
 * Print model analysis to console (useful for development)
 */
export function printModelAnalysis(analysis: ModelAnalysis, modelName: string): void {
    console.log(`\n========== Model Analysis: ${modelName} ==========`);
    console.log(`Original Size: ${analysis.originalSize.width.toFixed(2)} x ${analysis.originalSize.height.toFixed(2)} x ${analysis.originalSize.depth.toFixed(2)}`);
    console.log(`Vertex Count: ${analysis.vertexCount}`);
    console.log(`Mesh Count: ${analysis.meshCount}`);
    console.log(`Has Rig: ${analysis.hasRig} (${analysis.boneCount} bones)`);
    console.log(`Animations (${analysis.animations.length}):`);
    analysis.animations.forEach(a => console.log(`  - ${a}`));
    console.log(`Recommended Scale (for 2m height): ${analysis.recommendedScale.toFixed(4)}`);
    console.log(`Suggested Physics Shape: ${analysis.suggestedPhysicsShape}`);
    console.log(`Scaled Dimensions: ${analysis.suggestedPhysicsDimensions.width.toFixed(2)} x ${analysis.suggestedPhysicsDimensions.height.toFixed(2)} x ${analysis.suggestedPhysicsDimensions.depth.toFixed(2)}`);
    console.log(`================================================\n`);
}

/**
 * Generate asset catalog entry from analysis
 */
export function generateCatalogEntry(
    id: string,
    path: string,
    analysis: ModelAnalysis,
    targetHeight: number = 2
): string {
    const scale = calculateScaleForHeight(analysis.originalSize.height, targetHeight);
    
    return `
        ${id}: {
            id: "${id}",
            type: AssetType.MODEL,
            path: "${path}",
            name: "${id.charAt(0).toUpperCase() + id.slice(1)}",
            scale: ${scale.toFixed(4)},
            physics: {
                enabled: true,
                shapeType: "${analysis.suggestedPhysicsShape}",
                mass: 50,
                friction: 0.5,
                restitution: 0.1,
                boundingBox: {
                    width: ${(analysis.originalSize.width * scale).toFixed(2)},
                    height: ${(analysis.originalSize.height * scale).toFixed(2)},
                    depth: ${(analysis.originalSize.depth * scale).toFixed(2)},
                },
            },
            animations: {
                defaultAnimation: "${analysis.animations[0] || 'idle'}",
                loop: true,
                speed: 1.0,
                // Available animations: ${analysis.animations.join(', ')}
            },
        },`;
}
